<script setup lang="ts">
import { ref } from 'vue';
import { PrimaryButton } from '@hfc/ui-core';
import { OtpChannel } from '@/shared/domain/OtpChannel';
import { OtpChannelType } from '@/shared/domain/OtpChannelType';
import BaseSection from '@/application/shared/infrastructure/components/BaseSection.vue';
import { useAppStore } from '@/shared/infrastructure/composition/useAppStore';

interface EmitInterface {
  (event: 'channelSelected', channel: OtpChannelType): void
  (event: 'beforeSubmit'): void
}

const emits = defineEmits<EmitInterface>();

const { appState } = useAppStore();

const props = defineProps<{
  otpChannels: Array<OtpChannel>;
  loading: boolean;
}>();

const OtpChannelTypeClass = {
  [OtpChannelType.SMS]: {
    icon: 'comment-sms',
    color: 'text-green-600',
  },
  [OtpChannelType.EMAIL]: {
    icon: 'envelope',
    color: 'text-blue-600',
  }
};

const selectedChannel = ref<OtpChannel>(props.otpChannels.find(channel => channel.default) || props.otpChannels[0]);

const selectedClass = 'border-2 border-blue-600 hover:shadow-sm hover:shadow-blue-400';
const nonSelectedClass = 'border border-gray-300 hover:shadow-sm hover:shadow-gray-400';

const beforeSubmit = async (): Promise<void> => {
  emits('beforeSubmit');
};

defineExpose({ onSubmit: () => emits('channelSelected', selectedChannel.value.type) });
</script>
<template>
  <BaseSection>
    <div class="flex flex-col justify-center py-4 px-16">
      <div class="flex text-lg justify-center mb-6">
        Select your preferred method to receive your One Time Passcode
      </div>
      <div class="flex justify-center gap-10">
        <div v-for="otpChannel in otpChannels" :key="otpChannel.type" class="flex flex-col w-60 items-center text-xl space-y-2 px-10 py-4 rounded-md cursor-pointer" :class="selectedChannel && selectedChannel.type === otpChannel.type ? selectedClass : nonSelectedClass" @click="selectedChannel = otpChannel">
          <hfc-icon :icon="OtpChannelTypeClass[otpChannel.type].icon" class="text-5xl" :class="OtpChannelTypeClass[otpChannel.type].color" />
          <div>Send to {{ otpChannel.name }}</div>
          <div class="text-slate-500">{{ otpChannel.to }}</div>
        </div>
      </div>
    </div>
  </BaseSection>
  <div class="flex justify-center mt-10">
    <PrimaryButton @click="beforeSubmit" :square="false" size="xl" :is-loading="appState.loading || loading">
      Send Code
    </PrimaryButton>
  </div>
</template>
